import React from 'react';
import { Box } from '@mui/material';

const COLORS = {
  main: '#667eea',
  feature: '#11998e',
  hotfix: '#f44336',
  commit: '#764ba2',
  merge: '#ff9800',
  bg: 'transparent',
  text: '#444',
  textLight: '#888',
};

/**
 * BranchingBasicsDiagram — shows how branches fork from main and run in parallel.
 */
export function BranchingBasicsDiagram() {
  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
      <svg viewBox="0 0 520 200" width="100%" height="180" style={{ maxWidth: 520 }}>
        {/* Main branch line */}
        <line x1="40" y1="60" x2="480" y2="60" stroke={COLORS.main} strokeWidth="3" />
        <text x="12" y="64" fill={COLORS.main} fontSize="11" fontWeight="700">main</text>

        {/* Main branch commits */}
        {[80, 160, 320, 480].map((x, i) => (
          <g key={`mc${i}`}>
            <circle cx={x} cy={60} r={8} fill="white" stroke={COLORS.main} strokeWidth="2.5" />
            <text x={x} y={42} fill={COLORS.textLight} fontSize="9" textAnchor="middle">
              {['v1.0', 'v1.1', '', 'v1.2'][i]}
            </text>
          </g>
        ))}

        {/* Fork arrow from main to feature branch */}
        <path d="M 160 60 C 180 60, 180 140, 200 140" stroke={COLORS.feature} strokeWidth="2.5" fill="none" />

        {/* Feature branch line */}
        <line x1="200" y1="140" x2="360" y2="140" stroke={COLORS.feature} strokeWidth="3" strokeDasharray="6 3" />
        <text x="200" y="170" fill={COLORS.feature} fontSize="11" fontWeight="700">feature/cooling</text>

        {/* Feature branch commits */}
        {[240, 300, 360].map((x, i) => (
          <g key={`fc${i}`}>
            <circle cx={x} cy={140} r={7} fill="white" stroke={COLORS.feature} strokeWidth="2" />
            <text x={x} y={125} fill={COLORS.textLight} fontSize="8" textAnchor="middle">
              {['Add spec', 'Update', 'Fix'][i]}
            </text>
          </g>
        ))}

        {/* Parallel work annotation */}
        <text x="260" y="98" fill={COLORS.text} fontSize="10" textAnchor="middle" fontStyle="italic">
          Work happens in parallel
        </text>
        <path d="M 180 90 L 340 90" stroke={COLORS.textLight} strokeWidth="0.5" strokeDasharray="3 3" />
      </svg>
    </Box>
  );
}

/**
 * MergeDiagram — shows a feature branch merging back into main.
 */
export function MergeDiagram() {
  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
      <svg viewBox="0 0 520 220" width="100%" height="200" style={{ maxWidth: 520 }}>
        {/* Main branch line */}
        <line x1="40" y1="60" x2="480" y2="60" stroke={COLORS.main} strokeWidth="3" />
        <text x="12" y="64" fill={COLORS.main} fontSize="11" fontWeight="700">main</text>

        {/* Main commits */}
        {[80, 160, 400, 480].map((x, i) => (
          <g key={`mc${i}`}>
            <circle cx={x} cy={60} r={8} fill="white" stroke={COLORS.main} strokeWidth="2.5" />
          </g>
        ))}

        {/* Fork to feature */}
        <path d="M 160 60 C 180 60, 180 130, 200 130" stroke={COLORS.feature} strokeWidth="2.5" fill="none" />

        {/* Feature branch */}
        <line x1="200" y1="130" x2="340" y2="130" stroke={COLORS.feature} strokeWidth="3" strokeDasharray="6 3" />
        <text x="200" y="160" fill={COLORS.feature} fontSize="11" fontWeight="700">feature/cooling</text>

        {/* Feature commits */}
        {[240, 300, 340].map((x, i) => (
          <g key={`fc${i}`}>
            <circle cx={x} cy={130} r={7} fill="white" stroke={COLORS.feature} strokeWidth="2" />
          </g>
        ))}

        {/* Merge arrow back to main */}
        <path d="M 340 130 C 360 130, 380 60, 400 60" stroke={COLORS.merge} strokeWidth="2.5" fill="none" />

        {/* Merge commit (special) */}
        <circle cx={400} cy={60} r={10} fill={COLORS.merge} stroke="white" strokeWidth="2" />
        <text x={400} y={64} fill="white" fontSize="10" textAnchor="middle" fontWeight="700">M</text>

        {/* Merge label */}
        <text x={400} y={38} fill={COLORS.merge} fontSize="10" textAnchor="middle" fontWeight="600">
          Merge commit
        </text>

        {/* Arrow head decoration */}
        <polygon points="395,60 385,55 385,65" fill={COLORS.merge} />

        {/* Annotation */}
        <rect x="100" y="180" width="320" height="28" rx="4" fill="rgba(102,126,234,0.08)" />
        <text x="260" y="199" fill={COLORS.main} fontSize="10" textAnchor="middle" fontWeight="500">
          After merge, all feature work appears on the main branch
        </text>
      </svg>
    </Box>
  );
}

/**
 * BranchWorkflowDiagram — shows the full EIS workflow:
 * main → dev branch → feature branches → merge back → tag → release
 */
export function BranchWorkflowDiagram() {
  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
      <svg viewBox="0 0 520 260" width="100%" height="240" style={{ maxWidth: 520 }}>
        {/* Main branch */}
        <line x1="40" y1="40" x2="480" y2="40" stroke={COLORS.main} strokeWidth="3" />
        <text x="12" y="44" fill={COLORS.main} fontSize="11" fontWeight="700">main</text>
        {[80, 200, 420, 480].map((x, i) => (
          <circle key={`m${i}`} cx={x} cy={40} r={7} fill="white" stroke={COLORS.main} strokeWidth="2.5" />
        ))}

        {/* Dev branch */}
        <path d="M 80 40 C 90 40, 90 100, 100 100" stroke="#5c6bc0" strokeWidth="2" fill="none" />
        <line x1="100" y1="100" x2="380" y2="100" stroke="#5c6bc0" strokeWidth="2.5" strokeDasharray="6 3" />
        <text x="50" y="104" fill="#5c6bc0" fontSize="10" fontWeight="700">dev</text>
        {[140, 220, 300, 380].map((x, i) => (
          <circle key={`d${i}`} cx={x} cy={100} r={6} fill="white" stroke="#5c6bc0" strokeWidth="2" />
        ))}

        {/* Feature branch from dev */}
        <path d="M 140 100 C 150 100, 150 160, 160 160" stroke={COLORS.feature} strokeWidth="2" fill="none" />
        <line x1="160" y1="160" x2="280" y2="160" stroke={COLORS.feature} strokeWidth="2" strokeDasharray="4 3" />
        <text x="130" y="185" fill={COLORS.feature} fontSize="9" fontWeight="600">feature/thermal</text>
        {[200, 240, 280].map((x, i) => (
          <circle key={`f${i}`} cx={x} cy={160} r={5} fill="white" stroke={COLORS.feature} strokeWidth="2" />
        ))}

        {/* Merge feature → dev */}
        <path d="M 280 160 C 290 160, 295 100, 300 100" stroke={COLORS.merge} strokeWidth="2" fill="none" />

        {/* Merge dev → main */}
        <path d="M 380 100 C 395 100, 400 40, 420 40" stroke={COLORS.merge} strokeWidth="2.5" fill="none" />
        <circle cx={420} cy={40} r={9} fill={COLORS.merge} stroke="white" strokeWidth="2" />
        <text x={420} y={44} fill="white" fontSize="9" textAnchor="middle" fontWeight="700">M</text>

        {/* Tag */}
        <line x1="480" y1="40" x2="480" y2="15" stroke={COLORS.main} strokeWidth="1" />
        <rect x="460" y="2" width="40" height="16" rx="3" fill={COLORS.main} />
        <text x="480" y="14" fill="white" fontSize="9" textAnchor="middle" fontWeight="600">v2.0</text>

        {/* Workflow steps at bottom */}
        {[
          { x: 80, label: '1. Start', sub: 'Create project' },
          { x: 160, label: '2. Branch', sub: 'Create feature' },
          { x: 280, label: '3. Develop', sub: 'Make changes' },
          { x: 380, label: '4. Merge', sub: 'Review & merge' },
          { x: 480, label: '5. Release', sub: 'Tag & release' },
        ].map(({ x, label, sub }) => (
          <g key={label}>
            <text x={x} y="220" fill={COLORS.text} fontSize="9" textAnchor="middle" fontWeight="600">{label}</text>
            <text x={x} y="234" fill={COLORS.textLight} fontSize="8" textAnchor="middle">{sub}</text>
          </g>
        ))}

        {/* Flow arrows */}
        <line x1="110" y1="225" x2="135" y2="225" stroke={COLORS.textLight} strokeWidth="0.8" markerEnd="url(#arrow)" />
        <line x1="210" y1="225" x2="250" y2="225" stroke={COLORS.textLight} strokeWidth="0.8" markerEnd="url(#arrow)" />
        <line x1="320" y1="225" x2="350" y2="225" stroke={COLORS.textLight} strokeWidth="0.8" markerEnd="url(#arrow)" />
        <line x1="415" y1="225" x2="450" y2="225" stroke={COLORS.textLight} strokeWidth="0.8" markerEnd="url(#arrow)" />

        <defs>
          <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={COLORS.textLight} />
          </marker>
        </defs>
      </svg>
    </Box>
  );
}

/**
 * ConflictDiagram — shows what happens when two branches modify the same thing.
 */
export function ConflictDiagram() {
  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
      <svg viewBox="0 0 520 200" width="100%" height="180" style={{ maxWidth: 520 }}>
        {/* Main branch */}
        <line x1="40" y1="100" x2="480" y2="100" stroke={COLORS.main} strokeWidth="3" />
        <text x="12" y="104" fill={COLORS.main} fontSize="11" fontWeight="700">main</text>

        {/* Main commits */}
        {[80, 160, 420].map((x, i) => (
          <circle key={`m${i}`} cx={x} cy={100} r={7} fill="white" stroke={COLORS.main} strokeWidth="2.5" />
        ))}

        {/* Branch A (top) */}
        <path d="M 160 100 C 175 100, 180 45, 200 45" stroke={COLORS.feature} strokeWidth="2" fill="none" />
        <line x1="200" y1="45" x2="340" y2="45" stroke={COLORS.feature} strokeWidth="2" strokeDasharray="5 3" />
        <text x="240" y="32" fill={COLORS.feature} fontSize="9" fontWeight="600">Branch A: edits file.dxf</text>
        {[240, 300, 340].map((x, i) => (
          <circle key={`a${i}`} cx={x} cy={45} r={5} fill="white" stroke={COLORS.feature} strokeWidth="2" />
        ))}

        {/* Branch B (bottom) */}
        <path d="M 160 100 C 175 100, 180 155, 200 155" stroke={COLORS.hotfix} strokeWidth="2" fill="none" />
        <line x1="200" y1="155" x2="340" y2="155" stroke={COLORS.hotfix} strokeWidth="2" strokeDasharray="5 3" />
        <text x="240" y="178" fill={COLORS.hotfix} fontSize="9" fontWeight="600">Branch B: edits file.dxf</text>
        {[240, 300, 340].map((x, i) => (
          <circle key={`b${i}`} cx={x} cy={155} r={5} fill="white" stroke={COLORS.hotfix} strokeWidth="2" />
        ))}

        {/* Merge arrows converging */}
        <path d="M 340 45 C 370 45, 390 100, 420 100" stroke={COLORS.feature} strokeWidth="2" fill="none" />
        <path d="M 340 155 C 370 155, 390 100, 420 100" stroke={COLORS.hotfix} strokeWidth="2" fill="none" />

        {/* Conflict marker */}
        <circle cx={420} cy={100} r={12} fill="white" stroke={COLORS.hotfix} strokeWidth="2.5" />
        <text x={420} y={105} fill={COLORS.hotfix} fontSize="14" textAnchor="middle" fontWeight="700">!</text>

        {/* Conflict label */}
        <rect x="440" y="88" width="70" height="22" rx="4" fill="rgba(244,67,54,0.1)" />
        <text x="475" y="103" fill={COLORS.hotfix} fontSize="9" textAnchor="middle" fontWeight="600">
          Conflict
        </text>
      </svg>
    </Box>
  );
}
